import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, MapPin, Users, FileText, ArrowRight, LogOut } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { api, useAuth } from "@/auth/AuthContext";
import { fmt } from "@/journeys/CartContext";
import { useLang } from "@/contexts/LangContext";
import { jx } from "@/i18n/journeyLabels";

const Badge = ({ tone, children }) => (
  <span className={`inline-block px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] font-bold ${
    tone === "green" ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
    : tone === "amber" ? "bg-amber-50 text-amber-700 border border-amber-200"
    : "bg-[#F7F5F0] text-[#4A4A4A] border border-[#EAE6DF]"}`}>{children}</span>
);

const payTone = (s) => (s === "paid" ? "green" : s === "refunded" ? "grey" : "amber");
const statusTone = (s) => (["confirmed", "completed", "in_progress"].includes(s) ? "green" : ["cancelled", "refunded"].includes(s) ? "grey" : "amber");

export default function MyJourneys() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState(null);
  const { lang } = useLang();
  const L = jx(lang);

  useEffect(() => {
    if (user === null) navigate("/login?next=/my-journeys");
  }, [user, navigate]);

  useEffect(() => {
    if (user) api.get("/journeys/my/bookings").then((r) => setBookings(r.data.bookings)).catch(() => setBookings([]));
  }, [user]);

  if (!user) return <div className="min-h-screen flex items-center justify-center bg-[#F7F5F0]"><Loader2 className="animate-spin text-[#C62828]" size={28} /></div>;

  return (
    <div className="min-h-screen bg-[#F7F5F0] font-body text-[#121212]">
      <SEO title="My Journeys — The China Explorer" description="Your booked China journeys." path="/my-journeys" />
      <Header />
      <div className="max-w-5xl mx-auto px-6 md:px-12 pt-32 pb-20">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C62828] font-semibold">✦ {L.myWelcome}, {user.name}</p>
            <h1 className="font-heading text-4xl md:text-5xl text-[#0E2A5C] mt-2" data-testid="my-journeys-title">{L.myTitle}</h1>
          </div>
          <button onClick={async () => { await logout(); navigate("/"); }} data-testid="my-journeys-signout"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#4A4A4A] hover:text-[#C62828]">
            <LogOut size={13} /> {L.mySignOut}
          </button>
        </div>

        {bookings === null ? (
          <div className="mt-12 text-center"><Loader2 className="animate-spin mx-auto text-[#C62828]" size={28} /></div>
        ) : bookings.length === 0 ? (
          <div className="mt-10 bg-white border border-[#EAE6DF] p-12 text-center" data-testid="my-journeys-empty">
            <p className="text-[#4A4A4A]">{L.myEmpty}</p>
            <Link to="/journeys" className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#C62828]">{L.exploreJourneys} <ArrowRight size={13} /></Link>
          </div>
        ) : (
          <div className="mt-10 space-y-5" data-testid="my-journeys-list">
            {bookings.map((b) => (
              <div key={b.id} className="bg-white border border-[#EAE6DF] p-6 md:p-8" data-testid={`my-booking-${b.reference}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#C62828] font-bold" data-testid="my-booking-reference">{b.reference}</p>
                    <h2 className="font-heading text-2xl text-[#0E2A5C] mt-1">{b.title}</h2>
                    <p className="mt-1.5 text-sm text-[#4A4A4A] flex items-center gap-4 flex-wrap">
                      <span className="inline-flex items-center gap-1.5"><MapPin size={12} />{b.departure_label || b.preferred_date || L.myDateTBC}</span>
                      <span className="inline-flex items-center gap-1.5"><Users size={12} />{b.adults} {L.adultsShort}{b.children ? `, ${b.children} ${L.childrenShort}` : ""}{b.infants ? `, ${b.infants} ${L.infantsShort}` : ""} · {b.room_type} {L.roomShort}</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading text-2xl text-[#C62828]">{fmt(b.total, b.currency)}</p>
                    <div className="mt-2 flex gap-2 justify-end">
                      <Badge tone={payTone(b.payment_status)}>{b.payment_status}</Badge>
                      <Badge tone={statusTone(b.booking_status)}>{(b.booking_status || "").replace("_", " ")}</Badge>
                    </div>
                  </div>
                </div>
                {b.traveller_details?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-[#EAE6DF]">
                    <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#4A4A4A]">{L.myTravellers}</p>
                    <p className="mt-1 text-sm text-[#0E2A5C]">{b.traveller_details.map((t) => t.name).filter(Boolean).join(" · ")}</p>
                  </div>
                )}
                <div className="mt-4 pt-4 border-t border-[#EAE6DF] flex items-center gap-2 text-xs text-[#4A4A4A]">
                  <FileText size={13} className="text-[#D4AF37]" />
                  {L.myDocs}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
